import { AxleErrorBoundary } from '@/fetching/errorBoundaries';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import { isPaletteShortcut } from './paletteShortcut';

// Loaded only when the flag is on, so organizations without it never download the palette.
const CommandPalette = lazy(() =>
    import('./CommandPalette').then((module) => ({ default: module.CommandPalette })),
);

// Stands in for a palette that failed to load, so its shortcut goes back to the browser.
const StopHolding = ({ onMount }: { onMount: () => void }) => {
    useEffect(onMount, [onMount]);
    return null;
};

/** Mounts the command palette for organizations that have the flag. */
export const CommandPaletteMount = () => {
    const enabled = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_COMMAND_PALETTE_MVP);
    const [pressedWhileLoading, setPressedWhileLoading] = useState(false);
    const paletteReady = useRef(false);
    // Called once the palette hears the shortcut itself, or has failed to load and never will.
    const onReady = useCallback(() => {
        paletteReady.current = true;
        setPressedWhileLoading(false);
    }, []);

    // The palette loads on demand, so until it can hear the shortcut the browser would take it
    // (Ctrl+K focuses the address bar search). This holds the shortcut and opens the palette
    // once it has loaded.
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (paletteReady.current || !isPaletteShortcut(event)) {
                return;
            }
            event.preventDefault();
            setPressedWhileLoading(true);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [enabled]);

    if (!enabled) {
        return null;
    }

    // The palette is optional, so a failed load stays here instead of reaching the app-level boundary.
    return (
        <AxleErrorBoundary fallback={<StopHolding onMount={onReady} />}>
            <Suspense fallback={null}>
                <CommandPalette defaultOpen={pressedWhileLoading} onReady={onReady} />
            </Suspense>
        </AxleErrorBoundary>
    );
};
